import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Popover, message } from "antd";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import CustomBreadcrumb from "../../common/CustomBreadcrumb";
import {
  BtnBold,
  BtnBulletList,
  BtnItalic,
  BtnNumberedList,
  BtnStrikeThrough,
  BtnUnderline,
  Editor,
  EditorProvider,
  Toolbar,
} from "react-simple-wysiwyg";
import {
  BtnAlignCenter,
  BtnAlignLeft,
  BtnAlignRight,
  BtnSubscript,
  BtnSuperscript,
} from "../Html_Editor/BtnFontFamily";
import infoicon from "../../assets/infoicon.svg";
import LanguageSwitch from "../../common/LanguageSwitch";
import { ThemeContext } from "../../common/themeContext";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../Loder/loader";
import {
  updateEnrollmentAgreementAction,
  updateEnrollmentAgreementHandler,
} from "../../redux/action/updateEnrollmentAgreementSlice";
import {
  getEnrollmentAgreementDetailsAction,
  getEnrollmentAgreementDetailsHandler,
} from "../../redux/action/getEnrollmentAgreementDetailsSlice";

/** True when HTML is missing or only whitespace/empty block markup (e.g. `<p><br></p>`). */
function isHtmlContentEmpty(html) {
  if (html == null || typeof html !== "string") return true;
  const text = html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length === 0;
}

const EnrollmentAgreementView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { enrollmentAgreementId: enrollmentAgreementIdParam } = useParams();
  const { brandId } = useContext(ThemeContext);
  const updateState = useSelector((state) => state.updateEnrollmentAgreement);
  const detailsState = useSelector(
    (state) => state.getEnrollmentAgreementDetails,
  );
  const [activeKey, setActiveKey] = useState("text");
  const [infoPopoverVisible, setInfoPopoverVisible] = useState(false);
  const [manageSwitch, setManageSwitch] = useState(false);
  const [documentName, setDocumentName] = useState(
    "Enrollment Agreement",
  );
  const [textContent, setTextContent] = useState({
    english:
      "<p>Welcome to Healthcare2U!</p><p>Thank you for joining our healthcare platform. Your account has been created successfully.</p>",
    spanish: "",
  });
  const [documentContent, setDocumentContent] = useState({
    english: "",
    spanish: "",
  });
  const currentLang = manageSwitch ? "spanish" : "english";

  useEffect(() => {
    const idFromState = location?.state?.enrollmentAgreementId;
    const id = Number(idFromState ?? enrollmentAgreementIdParam);
    if (!Number.isFinite(id) || id <= 0) return;
    dispatch(
      getEnrollmentAgreementDetailsHandler({
        enrollmentAgreementId: id,
        refBrandId: brandId,
      }),
    );

    return () => {
      dispatch(
        getEnrollmentAgreementDetailsAction.getEnrollmentAgreementDetailsInfoReset(),
      );
    };
  }, [brandId, dispatch, enrollmentAgreementIdParam, location?.state?.enrollmentAgreementId]);

  useEffect(() => {
    const payload = detailsState?.data?.data ?? detailsState?.data;
    if (!payload) return;

    setDocumentName(payload?.documentName || "Enrollment Agreement");
    setTextContent({
      english: payload?.textContent?.english || "",
      spanish: payload?.textContent?.spanish || "",
    });
    setDocumentContent({
      english: payload?.documentContent?.english || "",
      spanish: payload?.documentContent?.spanish || "",
    });
  }, [detailsState?.data]);

  useEffect(() => {
    if (!updateState) return;
    if (updateState?.message) {
      message.success(updateState.message);
      dispatch(
        updateEnrollmentAgreementAction.updateEnrollmentAgreementInfoReset(),
      );
      navigate("/ai-campaigns/enrollment-agreement");
    }
    if (updateState?.error) {
      message.error(updateState.error);
      dispatch(
        updateEnrollmentAgreementAction.updateEnrollmentAgreementInfoReset(),
      );
    }
  }, [updateState, dispatch, navigate]);

  const handleSave = () => {
    const isTextEmpty =
      isHtmlContentEmpty(textContent?.english) ||
      isHtmlContentEmpty(textContent?.spanish);
    const isDocumentEmpty =
      isHtmlContentEmpty(documentContent?.english) ||
      isHtmlContentEmpty(documentContent?.spanish);

    if (isTextEmpty || isDocumentEmpty) {
      message.error("Enrollment Agreement content cannot be empty.");
      return;
    }

    const payload = {
      refBrandId: brandId,
      documentName,
      textContent,
      documentContent,
    };
    dispatch(updateEnrollmentAgreementHandler(payload));
  };

  const toolbar = useMemo(
    () => (
      <Toolbar>
        <BtnBold />
        <BtnItalic />
        <BtnUnderline />
        <BtnBulletList />
        <BtnNumberedList />
        <BtnStrikeThrough />
        <BtnAlignCenter />
        <BtnAlignLeft />
        <BtnAlignRight />
        <BtnSuperscript />
        <BtnSubscript />
      </Toolbar>
    ),
    [],
  );
  const infoContent = (
    <div style={{ maxWidth: "660px" }}>
      Use only the supported variable format for placeholders:
      <br />
      %userName%, %dateOfBirth%, %memberId%, %signature%, %date%, %day%,
      %month%, %year%.
      {" "}These variables will be automatically replaced with the corresponding values when the template is generated.
    </div>
  );

  return (
    <div className="rightside">
      <div className="rightcontent">
        {(updateState?.isLoading === true || detailsState?.isLoading === true) && (
          <Loader loaderTransform="loaderTransform" />
        )}
        <p>
          <strong>Enrollment Agreement</strong>
        </p>
        <CustomBreadcrumb
          items={[
            { text: "Home" },
            { text: "AI Campaigns", active: false, onClick: () => navigate("/ai-campaigns/campaign-list") },
            { text: "Enrollment Agreement", active: false, onClick: () => navigate("/ai-campaigns/enrollment-agreement") },
            { text: "View", active: true },
          ]}
        />
        <LanguageSwitch manageSwitch={manageSwitch} setManageSwitch={setManageSwitch} />
        <div className="table-padding tabbing mb-30 position-relative tabsetting">
          <Popover
            content={infoContent}
            trigger="click"
            open={infoPopoverVisible}
            onOpenChange={setInfoPopoverVisible}
            overlayClassName="custom-info-popover"
            placement="bottomRight"
          >
            <div className="infoiconpoistion ">
              <img src={infoicon} alt="Info" />
            </div>
          </Popover>
          <Tabs activeKey={activeKey} onSelect={(k) => setActiveKey(k || "text")} className="p-0">
            <Tab eventKey="text" title="HTML Editor - Text">
              <div>
                <EditorProvider>
                  <Editor
                    value={textContent[currentLang]}
                    onChange={(e) =>
                      setTextContent((prev) => ({
                        ...prev,
                        [currentLang]: e.target.value,
                      }))
                    }
                    className="editorContent"
                  >
                    {toolbar}
                  </Editor>
                </EditorProvider>
              </div>
            </Tab>
            <Tab eventKey="document" title="HTML Editor - Document">
              <div>
                <EditorProvider>
                  <Editor
                    value={documentContent[currentLang]}
                    onChange={(e) =>
                      setDocumentContent((prev) => ({
                        ...prev,
                        [currentLang]: e.target.value,
                      }))
                    }
                    className="editorContent"
                  >
                    {toolbar}
                  </Editor>
                </EditorProvider>
              </div>
            </Tab>
          </Tabs>
        </div>

        <div className="d-flex justify-content-end align-items-center gap-16">
          <button
            type="button"
            className="cancelbtn primarybtn w130 fs-18 "
            onClick={() => navigate("/ai-campaigns/enrollment-agreement")}
          >
            Cancel
          </button>
          <button type="button" className=" primarybtn fs-18 w130" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnrollmentAgreementView;